import React, { useContext, useMemo } from "react";
import moment from "moment";
import { useSelector } from "react-redux";
import { useTheme } from "styled-components";
import { FilterType, LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { closedTradesFilters } from "@shared/slices/filters.slice";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { SocketPnlContext } from "@shared/features/dashboard/SocketPnl.context";
import { useTotalPnl } from "@shared/features/socket/hooks";
import { TradesPageContext } from "@shared/features/trades/common/TradesPage.context";
import { DISPLAY_CURRENCIES } from "common/constants";
import { PHONE_SUMMARY_CARD_ID } from "testIds";
import useClosedPnlChartSlots from "features/trades/NewLook/useClosedPnlChartSlots.tsx";
import { directionColor, getNewLookTokens } from "common/NewLook/tokens.ts";
import {
  getTopShares,
  PhoneAllocationCard,
  phoneMoney,
  toSplitSegments,
  usePhoneRestColor,
} from "features/dashboard/PhoneDashboard/PhoneSummary";
import { formatDateLabel } from "@shared/utils/date";

/** Phone open trades: the amount in trades split by pair. */
export const PhoneOpenTradesHero = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const restColor = usePhoneRestColor();
  const isBtc = useSelector(selectDisplayCurrency) === DISPLAY_CURRENCIES.BTC;
  const symbol = isBtc ? "₿" : "$";
  const { getDisplayCurrencyAmountPrecision } = usePrecisions();
  const precision = getDisplayCurrencyAmountPrecision();
  const { filteredOpenTradesData, isLoading } = useContext(TradesPageContext)!;
  const { socketPnlValue } = useContext(SocketPnlContext) || {};
  const trades = useMemo(
    () => filteredOpenTradesData ?? [],
    [filteredOpenTradesData]
  );
  const pnl = useTotalPnl(trades, socketPnlValue);

  const { total, hot, shares } = useMemo(() => {
    const key = isBtc ? "allocated_amount_btc" : "allocated_amount_usd";
    const total = trades.reduce((sum, trade) => sum + (trade[key] || 0), 0);
    const hot = trades.filter((trade) => trade.closest_order?.is_hot).length;
    const shares = getTopShares(
      trades.map((trade) => ({
        key: String(trade.id),
        name: trade.symbol,
        group: trade.symbol,
        value: trade[key] || 0,
      })),
      4,
      t("phone.other"),
      restColor
    );
    return { total, hot, shares };
  }, [trades, isBtc, restColor, t]);

  const meta = [
    t("phone.pnl_unrealized", {
      pnl: phoneMoney(pnl, symbol, precision, true),
    }),
    hot > 0 ? t("phone.hot_count", { count: hot }) : undefined,
  ]
    .filter(Boolean)
    .join(" · ");

  if (!isLoading && !trades.length) return null;
  return (
    <PhoneAllocationCard
      label={t("phone.open_amount")}
      value={phoneMoney(total, symbol, precision)}
      loading={isLoading && !trades.length}
      meta={meta}
      segments={toSplitSegments(shares)}
      testId={PHONE_SUMMARY_CARD_ID}
    />
  );
};

/** Phone closed trades: the realized PnL of the selected period, wins and losses. */
export const PhoneClosedTradesHero = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const tokens = getNewLookTokens(useTheme());
  const isBtc = useSelector(selectDisplayCurrency) === DISPLAY_CURRENCIES.BTC;
  const symbol = isBtc ? "₿" : "$";
  const { getDisplayCurrencyAmountPrecision } = usePrecisions();
  const precision = getDisplayCurrencyAmountPrecision();
  const { getTradesQuery, activeTrades, isLoading } =
    useContext(TradesPageContext)!;
  const chartSlots = useClosedPnlChartSlots();
  const filters = useSelector(closedTradesFilters.selectors.selectFiltersData);
  const range = (
    filters?.[FilterType.TIME_FRAME]?.value as { range?: number[] } | undefined
  )?.range;
  const period =
    range && range[0] && range[1]
      ? `${formatDateLabel(moment.unix(range[0]), "MMM D")} – ${formatDateLabel(
          moment.unix(range[1]),
          "MMM D"
        )}`
      : t("phone.all_time");

  const aggregated = getTradesQuery?.data?.aggregated_data;
  const change = aggregated?.change?.[isBtc ? "BTC" : "USD"] ?? 0;
  const count =
    aggregated?.number_of_closed_trades ?? activeTrades?.length ?? 0;
  const key = isBtc ? "btc_amount_change" : "usd_amount_change";
  const { wins, losses } = useMemo(() => {
    let wins = 0;
    let losses = 0;
    (activeTrades ?? []).forEach((trade) => {
      if ((trade[key] || 0) > 0) wins += 1;
      else if ((trade[key] || 0) < 0) losses += 1;
    });
    return { wins, losses };
  }, [activeTrades, key]);
  // The count and PnL are server aggregates over the whole period, but the
  // server sends at most the latest 200 closed trades and has no win/loss
  // aggregate. Wins, losses and the win rate are only shown when the loaded
  // rows cover every closed trade, so all figures share one basis.
  const isComplete = (activeTrades?.length ?? 0) >= count;
  const decided = isComplete ? wins + losses : 0;
  const meta = decided
    ? t("phone.win_rate", { rate: `${Math.round((wins / decided) * 100)}%` })
    : undefined;

  const pending = isLoading && !aggregated;
  return (
    <PhoneAllocationCard
      label={`${t("phone.realized_pnl")} · ${period}`}
      value={phoneMoney(change, symbol, precision, true)}
      valueColor={directionColor(tokens, change)}
      loading={pending}
      meta={meta}
      testId={PHONE_SUMMARY_CARD_ID}
      {...chartSlots}
      segments={
        decided
          ? [
              {
                key: "wins",
                name: `${t("phone.wins")} · ${wins}`,
                color: tokens.up,
                share: wins / decided,
              },
              {
                key: "losses",
                name: `${t("phone.losses")} · ${losses}`,
                color: tokens.down,
                share: losses / decided,
              },
            ].filter((segment) => segment.share > 0)
          : []
      }
    />
  );
};
